/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { Feature, debug } from 'application/Shared/Utils';

/*  React  */
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';

/* Components */
import { IntegrationCard } from './Pages/Components/IntegrationCard';

/* Helpers */
import { getIntegrationsData } from './Pages/Helpers/utils';

class PartnerIntegrations extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            deployment: [],
            loaded: false,
            searching: false,
            searchVal: '',
            showData: true,
            showDeployment: true,
            showWorkflow: true,
            workflow: []
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        try {
            const response = await getIntegrationsData();
            const { workflow, deployment, data } = response;

            this.setState({ data, deployment, loaded: true, workflow });
        } catch (e) {
            debug.e('Unable to load Integrations', e);
        }
    };

    search = async () => {
        this.setState((prevState) => {
            let { workflow, deployment, data } = prevState;
            const v = prevState.searchVal;

            workflow = workflow.map((d) => {
                const a = d.application.toLowerCase();
                const b = d.description.toLowerCase();

                if (a.indexOf(v) > -1 || b.indexOf(v) > -1) {
                    return { ...d, show: true };
                }
                return { ...d, show: false };
            });

            deployment = deployment.map((d) => {
                const a = d.application.toLowerCase();
                const b = d.description.toLowerCase();

                if (a.indexOf(v) > -1 || b.indexOf(v) > -1) {
                    return { ...d, show: true };
                }
                return { ...d, show: false };
            });

            data = data.map((d) => {
                const a = d.application.toLowerCase();
                const b = d.description.toLowerCase();

                if (a.indexOf(v) > -1 || b.indexOf(v) > -1) {
                    return { ...d, show: true };
                }
                return { ...d, show: false };
            });

            return {
                data,
                deployment,
                searching: false,
                workflow
            };
        });
    };

    renderWorkflow = () => {
        const { t } = this.props;
        const { workflow, showWorkflow } = this.state;

        return (
            <Accordion
                expanded={showWorkflow}
                onChange={(_e, v) => this.setState({ showWorkflow: v })}
                slotProps={{ transition: { unmountOnExit: true } }}
            >
                <AccordionSummary
                    expandIcon={
                        <IconWithVariant
                            data-testid="expandAccordionIconPartnerIntegrationsWorkflow"
                            variant="primary"
                            src="ChevronDown"
                            size={FontSizes[2]}
                        />
                    }
                >
                    <Box display="flex" alignItems="center">
                        <IconWithVariant
                            variant="primary"
                            src="Insights"
                            size={FontSizes[2]}
                            wrapperProps={{ marginRight: '8px' }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('Integrations.workflow')}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        {workflow.map((integration) => (
                            <IntegrationCard
                                integration={integration}
                                key={`renderIntegration_${integration.application}`}
                            />
                        ))}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    renderDeployment = () => {
        const { t } = this.props;
        const { deployment, showDeployment } = this.state;

        return (
            <Accordion
                expanded={showDeployment}
                onChange={(_e, v) => this.setState({ showDeployment: v })}
                slotProps={{ transition: { unmountOnExit: true } }}
            >
                <AccordionSummary
                    expandIcon={
                        <IconWithVariant
                            data-testid="expandAccordionIconPartnerIntegrationsDeployment"
                            variant="primary"
                            src="ChevronDown"
                            size={FontSizes[2]}
                        />
                    }
                >
                    <Box display="flex" alignItems="center">
                        <IconWithVariant
                            variant="primary"
                            src="Laptop"
                            size={FontSizes[2]}
                            wrapperProps={{ marginRight: '8px' }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('Integrations.deployment')}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        {deployment.map((integration) => (
                            <IntegrationCard
                                integration={integration}
                                key={`renderIntegration_${integration.application}`}
                            />
                        ))}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    renderData = () => {
        const { t, session } = this.props;
        const { data, showData } = this.state;

        // Feature Check
        const feature = new Feature('dataIntegrations');
        if (!feature.isEnabled(session)) {
            return null;
        }

        return (
            <Accordion
                expanded={showData}
                onChange={(_e, v) => this.setState({ showData: v })}
                slotProps={{ transition: { unmountOnExit: true } }}
            >
                <AccordionSummary
                    expandIcon={
                        <IconWithVariant
                            data-testid="expandAccordionIconPartnerIntegrationsData"
                            variant="primary"
                            src="ChevronDown"
                            size={FontSizes[2]}
                        />
                    }
                >
                    <Box display="flex" alignItems="center">
                        <IconWithVariant
                            variant="primary"
                            src="PuzzlePiece"
                            size={FontSizes[2]}
                            wrapperProps={{ marginRight: '8px' }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('Integrations.data')}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        {data.map((integration) => (
                            <IntegrationCard
                                integration={integration}
                                key={`renderIntegration_${integration.application}`}
                            />
                        ))}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    renderIntegrations = () => {
        const { searching } = this.state;
        if (searching) {
            return null;
        }

        return (
            <>
                <Box>{this.renderDeployment()}</Box>
                <Box mt="16px">{this.renderWorkflow()}</Box>
                <Box mt="16px">{this.renderData()}</Box>
            </>
        );
    };

    render() {
        const { t } = this.props;
        const { loaded } = this.state;

        return (
            <Box width="100%" data-testid="partnerIntegrations">
                {loaded && (
                    <>
                        <Box mb="16px">
                            <Card variant="outlined">
                                <CardContent>
                                    <TextField
                                        autoComplete="off"
                                        sx={{ width: '40%' }}
                                        InputProps={{
                                            startAdornment: (
                                                <IconWithVariant
                                                    variant="search"
                                                    src="Search"
                                                    size={FontSizes[1]}
                                                    wrapperProps={{
                                                        marginRight: '4px'
                                                    }}
                                                />
                                            )
                                        }}
                                        onChange={(e) => {
                                            this.setState(
                                                {
                                                    searching: true,
                                                    searchVal:
                                                        e.target.value.toLowerCase()
                                                },
                                                () => {
                                                    this.search();
                                                }
                                            );
                                        }}
                                        placeholder={t('Common.search')}
                                        variant="outlined"
                                        size="small"
                                    />
                                </CardContent>
                            </Card>
                        </Box>
                        {this.renderIntegrations()}
                    </>
                )}
            </Box>
        );
    }
}
export default withTranslation()(PartnerIntegrations);
